import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, BlockStack } from "@shopify/polaris";
import DatePickerx from "features/datepicker/date-picker";
import { approveMedicalCheck } from "services/medical-check.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";

interface ApproveMedicalCheckModalProps {
  companyId: string;
  checkId: string;
  onApproved?: () => void;
}

const ApproveMedicalCheckModal: React.FC<ApproveMedicalCheckModalProps> = ({
  companyId,
  checkId,
  onApproved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [validFrom, setValidFrom] = useState<number | null>(null);
  const [validUntil, setValidUntil] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleApprove = async () => {
    if (!companyId || !checkId || !validFrom || !validUntil) return;
    if (validFrom > validUntil) {
      toast.error(t("medical_validity_invalid"));
      return;
    }
    setSubmitting(true);
    try {
      await approveMedicalCheck(companyId, checkId, { validFrom, validUntil });
      toast.success(t("medical_check_approved_toast"));
      onApproved?.();
      close();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || e?.response?.data?.message || t("error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <BlockStack gap="400">
        <p className="text-sm text-gray-600">{t("enter_medical_validity")}</p>
        <DatePickerx
          title={t("medical_valid_from")}
          selected={validFrom ? new Date(validFrom).toISOString().split("T")[0] : ""}
          onChange={(d: Date | null) => setValidFrom(d ? d.getTime() : null)}
          isRequired
          disableDatesAfter={null}
          fullWidth
        />
        <DatePickerx
          title={t("medical_valid_until")}
          selected={validUntil ? new Date(validUntil).toISOString().split("T")[0] : ""}
          onChange={(d: Date | null) => setValidUntil(d ? d.getTime() : null)}
          isRequired
          disableDatesAfter={null}
          disableDatesBefore={(validFrom ? new Date(validFrom) : undefined) as any}
          fullWidth
        />
      </BlockStack>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <Button onClick={close} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleApprove}
          loading={submitting}
          disabled={!validFrom || !validUntil}
        >
          {t("approve_medical")}
        </Button>
      </div>
    </div>
  );
};

export default ApproveMedicalCheckModal;
